'use client';

import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useSetAtom } from 'jotai';
import { Dices, Search, SendHorizonal } from 'lucide-react';
import { useSession } from '@/lib/auth-client';
import { pendingConversationAtom } from '@/_state/atoms';
import { Button } from '@/_components/UIComponents/Button';
import { EXAMPLE_CATEGORIES } from '@/_components/SearchExamples/searchExamplesData';

const ALL_EXAMPLE_QUERIES = EXAMPLE_CATEGORIES.flatMap((cat) => cat.examples.map((e) => e.query));
const SHORT_EXAMPLE_QUERIES = ALL_EXAMPLE_QUERIES.filter((q) => q.length <= 55);

function AnimatedPlaceholder() {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [displayed, setDisplayed] = useState('');
	const [isDeleting, setIsDeleting] = useState(false);

	useEffect(() => {
		const text = SHORT_EXAMPLE_QUERIES[currentIndex];

		if (!isDeleting) {
			// Typing
			if (displayed.length < text.length) {
				const timer = setTimeout(() => setDisplayed(text.slice(0, displayed.length + 1)), 40);
				return () => clearTimeout(timer);
			}
			// Pause at full text, then start deleting
			const timer = setTimeout(() => setIsDeleting(true), 2500);
			return () => clearTimeout(timer);
		}

		// Deleting
		if (displayed.length > 0) {
			const timer = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 20);
			return () => clearTimeout(timer);
		}
		// Move to next example
		setIsDeleting(false);
		setCurrentIndex((prev) => (prev + 1) % SHORT_EXAMPLE_QUERIES.length);
	}, [displayed, isDeleting, currentIndex]);

	return (
		<span className='text-muted-foreground/50 pointer-events-none select-none'>
			{displayed}
			<span className='animate-pulse'>|</span>
		</span>
	);
}

export default function LandingSearchForm() {
	const [value, setValue] = useState('');
	const inputRef = useRef<HTMLInputElement>(null);
	const router = useRouter();
	const { data: session, isPending: isAuthPending } = useSession();
	const setPendingConversation = useSetAtom(pendingConversationAtom);

	const handleSubmit = useCallback(() => {
		const trimmed = value.trim();
		if (!trimmed || isAuthPending) return;

		if (session) {
			// Authenticated — create conversation directly (same pattern as SearchForm)
			const newConversationId = crypto.randomUUID();
			setPendingConversation({ conversationId: newConversationId, prompt: trimmed });
			setValue('');
			router.push(`/conversation/${newConversationId}`);
		} else {
			// Unauthenticated — redirect to login with query preserved
			router.push(`/login?q=${encodeURIComponent(trimmed)}`);
		}
	}, [value, isAuthPending, session, setPendingConversation, router]);

	const handleRandomize = useCallback(() => {
		const randomQuery = ALL_EXAMPLE_QUERIES[Math.floor(Math.random() * ALL_EXAMPLE_QUERIES.length)];
		setValue(randomQuery);
		inputRef.current?.focus();
	}, []);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				handleSubmit();
			}
		},
		[handleSubmit]
	);

	return (
		<label className='group relative flex cursor-text items-center gap-3 rounded-xl border border-pink-500/20 bg-white/5 px-4 py-3 shadow-[0_0_30px_-5px_rgba(236,72,153,0.15)] backdrop-blur-md transition-all duration-300 focus-within:border-pink-500/40 focus-within:shadow-[0_0_50px_-5px_rgba(236,72,153,0.3)] hover:border-pink-500/30 hover:shadow-[0_0_40px_-5px_rgba(236,72,153,0.25)] sm:px-5 sm:py-4'>
			<Search className='text-muted-foreground/40 h-5 w-5 shrink-0 sm:h-6 sm:w-6' />

			<div className='relative min-h-[1.75rem] flex-1'>
				{/* Animated placeholder — hidden when user types */}
				{!value && (
					<div className='absolute inset-0 flex items-center overflow-hidden text-sm whitespace-nowrap sm:text-base'>
						<AnimatedPlaceholder />
					</div>
				)}
				<input
					ref={inputRef}
					type='text'
					aria-label='Search Polkadot'
					className='text-foreground relative z-10 w-full bg-transparent text-sm outline-none placeholder:text-transparent sm:text-base'
					value={value}
					onChange={(e) => setValue(e.target.value)}
					onKeyDown={handleKeyDown}
				/>
			</div>

			<Button
				variant='ghost'
				className='text-muted-foreground hover:text-foreground h-8 w-8 shrink-0 cursor-pointer'
				onClick={handleRandomize}
				aria-label='Random query'
				title='Try a random query'
			>
				<Dices className='h-4 w-4 sm:h-5 sm:w-5' />
			</Button>
			<Button
				variant='ghost'
				className='h-8 w-8 shrink-0 cursor-pointer rounded-lg bg-pink-600/80 text-white hover:bg-pink-500 disabled:bg-pink-600/20 disabled:text-white/30 sm:h-10 sm:w-10'
				onClick={handleSubmit}
				disabled={!value.trim() || isAuthPending}
				aria-label='Submit query'
			>
				<SendHorizonal className='h-4 w-4 sm:h-5 sm:w-5' />
			</Button>
		</label>
	);
}
